import { ApiError } from '@/handleApiError'
import { isGeneratedQuotesDomain, isGeneratedShopDomain } from '@/helpers'
import api from '@/modules/portalSettings/api'
import { getPortalLayoutType } from '@/modules/portalSettings/helpers'
import { Branding, PortalLayoutType, PortalTypeConfig } from '@/modules/portalSettings/types'
import Vuetify from '@/plugins/vuetify'
import { B2B_COMMERCE_PROMOTIONAL_LINK, QUOTES_PROMOTIONAL_LINK } from '@/types'
import { ComputedRef, computed, reactive } from 'vue'

interface BrandingState {
  branding: Branding | undefined
  loading: boolean
}

type ReturnType = {
  loading: ComputedRef<boolean>
  branding: ComputedRef<Branding | undefined>
  getBranding: () => Promise<PortalTypeConfig>
  setBranding: (branding: Branding) => void
  quotesPortalRegistrationAllowed: ComputedRef<boolean>
}

const state: BrandingState = reactive({
  branding: undefined,
  loading: false
})

export default function useBranding(): ReturnType {
  const getBranding = async (): Promise<PortalTypeConfig> => {
    state.loading = true

    return api
      .getBranding()
      .then(({ data }) => {
        if (typeof data == 'object') {
          state.branding = data

          if (state.branding.primaryColorHexCode) {
            const theme = Vuetify.theme.themes.value?.StorefrontLightTheme
            if (theme?.colors) {
              theme.colors.primary = state.branding.primaryColorHexCode
            }
          }

          return {
            portalLayoutType: getPortalLayoutType(data.portalType),
            hasGuestQuotes: data.hasGuestQuotes
          }
        } else {
          return {
            portalLayoutType: PortalLayoutType.PORTAL_UNAVAILABLE_LAYOUT,
            hasGuestQuotes: false
          }
        }
      })
      .catch((error: ApiError) => {
        // when no portal is found, redirect to ITscope B2B Commerce info page
        if (error.code === 400) {
          if (isGeneratedShopDomain.value) {
            window.location.href = B2B_COMMERCE_PROMOTIONAL_LINK
          } else if (isGeneratedQuotesDomain.value) {
            window.location.href = QUOTES_PROMOTIONAL_LINK
          }
        }

        return {
          portalLayoutType: PortalLayoutType.PORTAL_UNAVAILABLE_LAYOUT,
          hasGuestQuotes: false
        }
      })
      .finally(() => {
        state.loading = false
      })
  }

  return {
    loading: computed(() => state.loading),
    branding: computed(() => state.branding),
    getBranding,
    setBranding: (branding: Branding) => {
      state.branding = branding
    },
    quotesPortalRegistrationAllowed: computed(() => {
      return state.branding?.isLoginAllowed ?? true
    })
  }
}
